import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Shield, Users, AlertTriangle, CheckCircle2, Clock, FileText, Lock, ChevronDown, ChevronUp, Search, RefreshCw, Download, Settings, UserCheck, ShieldAlert, Activity, XCircle, Plus, Upload, Grid3X3 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  GREEN: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Compliant' },
  AMBER: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'Review' },
  RED: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Non-Compliant' },
  GREY: { color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', label: 'Not Assessed' },
};

const DECISION_CFG = {
  AUTHORISED: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  REVIEW: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: <Clock className="w-3.5 h-3.5" /> },
  BLOCKED: { color: 'text-red-400', bg: 'bg-red-500/10', icon: <XCircle className="w-3.5 h-3.5" /> },
  AUTHORISED_EXCEPTION: { color: 'text-sky-400', bg: 'bg-sky-500/10', icon: <ShieldAlert className="w-3.5 h-3.5" /> },
};

function StatCard({ label, value, sub, color = 'text-foreground', icon, testId }) {
  return (
    <Card className="glass-card border-border" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <span className={`text-2xl font-bold ${color}`}>{value}</span>
        </div>
        <p className="text-xs text-muted-foreground">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.GREY;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      {status}
    </span>
  );
}

function DecisionBadge({ decision }) {
  const cfg = DECISION_CFG[decision] || DECISION_CFG.REVIEW;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color}`}>
      {cfg.icon}{decision.replace('_', ' ')}
    </span>
  );
}

export default function HRComplianceDashboard() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [roles, setRoles] = useState([]);
  const [trainingReqs, setTrainingReqs] = useState([]);
  const [deploymentChecks, setDeploymentChecks] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  // Role-Requirement Matrix
  const [matrixRole, setMatrixRole] = useState(null);
  const [roleReqs, setRoleReqs] = useState([]);
  // Bulk Import
  const [csvText, setCsvText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Documents
  const [docList, setDocList] = useState(null);
  // Auto-trigger log
  const [triggerLog, setTriggerLog] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchRequirements();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/hr-compliance/dashboard`, { withCredentials: true });
      setDashboard(data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
    }
    // Also fetch auto-trigger log
    try {
      const { data: logData } = await axios.get(`${API}/api/hr-compliance/auto-trigger-log?limit=20`, { withCredentials: true });
      setTriggerLog(logData.events || []);
    } catch { /* */ }
    setLoading(false);
  };

  const fetchRequirements = async () => {
    try {
      const { data } = await axios.get(`${API}/api/hr-compliance/requirements`, { withCredentials: true });
      setRequirements(data);
    } catch { /* */ }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await axios.get(`${API}/api/hr-compliance/roles`, { withCredentials: true });
      setRoles(data);
    } catch { /* */ }
  };

  const fetchTrainingReqs = async () => {
    try {
      const { data } = await axios.get(`${API}/api/hr-compliance/training-requirements`, { withCredentials: true });
      setTrainingReqs(data);
    } catch { /* */ }
  };

  const fetchDeploymentChecks = async () => {
    try {
      const { data } = await axios.get(`${API}/api/hr-deployment/checks`, { withCredentials: true });
      setDeploymentChecks(data);
    } catch { /* */ }
  };

  const fetchExceptions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/hr-deployment/exceptions`, { withCredentials: true });
      setExceptions(data);
    } catch { /* */ }
  };

  const fetchStaffList = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/caregivers`, { withCredentials: true });
      setStaffList(data || []);
    } catch { setStaffList([]); }
  };

  const fetchRoleReqs = async (roleId) => {
    try {
      const { data } = await axios.get(`${API}/api/hr-compliance/role-requirements/${roleId}`, { withCredentials: true });
      setRoleReqs(data);
    } catch { setRoleReqs([]); }
  };

  const toggleRoleReq = async (roleId, reqId, isActive) => {
    const current = [...roleReqs];
    let updated;
    if (isActive) {
      updated = current.filter(r => r.requirement_id !== reqId);
    } else {
      updated = [...current, { requirement_id: reqId, applicability: 'MANDATORY', deployment_critical_override: null, conditions_json: {} }];
    }
    try {
      await axios.put(`${API}/api/hr-compliance/role-requirements/${roleId}`, { requirements: updated }, { withCredentials: true });
      setRoleReqs(updated);
      toast.success('Role requirement updated');
    } catch { toast.error('Update failed'); }
  };

  const runBulkImport = async () => {
    if (!csvText.trim()) { toast.error('Paste CSV data first'); return; }
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/api/hr-compliance/bulk-import`, { csv_data: csvText }, { withCredentials: true });
      setImportResult(data);
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  const fetchDocuments = async () => {
    try {
      const { data } = await axios.get(`${API}/api/hr-compliance/documents/list`, { withCredentials: true });
      setDocList(data);
    } catch (err) {
      if (err.response?.status === 402) {
        setDocList({ paywall: true, message: err.response.data?.detail });
      }
    }
  };

  const fetchStaffProfile = async (staffId) => {
    try {
      const { data } = await axios.get(`${API}/api/hr-compliance/staff/${staffId}/compliance`, { withCredentials: true });
      setStaffProfile(data);
      setSelectedStaff(staffId);
    } catch (err) { toast.error('Failed to load staff profile'); }
  };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/api/hr-compliance/seed-defaults`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchRequirements();
      fetchRoles();
      fetchTrainingReqs();
      fetchDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Seed failed'); }
    setSeeding(false);
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <Activity className="w-4 h-4" /> },
    { id: 'requirements', label: 'Requirements', icon: <Shield className="w-4 h-4" /> },
    { id: 'roles', label: 'Roles', icon: <Users className="w-4 h-4" /> },
    { id: 'training', label: 'Training', icon: <FileText className="w-4 h-4" /> },
    { id: 'workforce', label: 'Workforce', icon: <UserCheck className="w-4 h-4" /> },
    { id: 'deployment', label: 'Deployment Gate', icon: <Lock className="w-4 h-4" /> },
    { id: 'exceptions', label: 'Exceptions', icon: <ShieldAlert className="w-4 h-4" /> },
    { id: 'matrix', label: 'Role Matrix', icon: <Grid3X3 className="w-4 h-4" /> },
    { id: 'import', label: 'Bulk Import', icon: <Upload className="w-4 h-4" /> },
    { id: 'documents', label: 'Documents', icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="hr-compliance-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#00A6A0]" />
              HR Compliance & Deployment
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Compliance-driven workforce deployment — CQC inspection ready
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-border text-xs" onClick={seedDefaults} disabled={seeding} data-testid="seed-hr-defaults">
              <RefreshCw className={`w-4 h-4 mr-1 ${seeding ? 'animate-spin' : ''}`} />{seeding ? 'Seeding...' : 'Seed Defaults'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg overflow-x-auto scrollbar-hide">
          {tabs.map(t => (
            <button key={t.id} onClick={() => {
              setTab(t.id);
              if (t.id === 'roles') fetchRoles();
              if (t.id === 'training') fetchTrainingReqs();
              if (t.id === 'deployment') fetchDeploymentChecks();
              if (t.id === 'exceptions') fetchExceptions();
              if (t.id === 'workforce') fetchStaffList();
              if (t.id === 'dashboard') fetchDashboard();
              if (t.id === 'matrix') { fetchRoles(); fetchRequirements(); }
              if (t.id === 'documents') fetchDocuments();
            }}
              className={`flex items-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
              data-testid={`tab-${t.id}`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* ====== DASHBOARD TAB ====== */}
        {tab === 'dashboard' && (
          loading ? <div className="text-center py-12"><p className="text-muted-foreground">Loading dashboard...</p></div> : !dashboard ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground">No compliance data yet</p>
              <p className="text-xs text-muted-foreground mt-1">Click "Seed Defaults" to set up roles and requirements, then add staff compliance records</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Workforce Compliance Overview */}
              <div>
                <h2 className="text-sm font-medium text-foreground mb-3">Workforce Compliance</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <StatCard label="GREEN" value={dashboard.workforce.green} sub={`${dashboard.workforce.green_pct}%`} color="text-emerald-400" icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} testId="stat-green" />
                  <StatCard label="AMBER" value={dashboard.workforce.amber} sub={`${dashboard.workforce.amber_pct}%`} color="text-amber-400" icon={<Clock className="w-4 h-4 text-amber-400" />} testId="stat-amber" />
                  <StatCard label="RED" value={dashboard.workforce.red} sub={`${dashboard.workforce.red_pct}%`} color="text-red-400" icon={<XCircle className="w-4 h-4 text-red-400" />} testId="stat-red" />
                  <StatCard label="Total Records" value={dashboard.workforce.total} color="text-foreground" icon={<FileText className="w-4 h-4 text-muted-foreground" />} testId="stat-total" />
                  <StatCard label="Total Staff" value={dashboard.total_staff} color="text-[#00A6A0]" icon={<Users className="w-4 h-4 text-[#00A6A0]" />} testId="stat-staff" />
                </div>
              </div>

              {/* Expiry Pipeline */}
              <div>
                <h2 className="text-sm font-medium text-foreground mb-3">Expiry Pipeline</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard label="90 Days" value={dashboard.expiry_pipeline['90_days']} color="text-sky-400" icon={<Clock className="w-4 h-4 text-sky-400" />} testId="expiry-90" />
                  <StatCard label="60 Days" value={dashboard.expiry_pipeline['60_days']} color="text-amber-400" icon={<Clock className="w-4 h-4 text-amber-400" />} testId="expiry-60" />
                  <StatCard label="30 Days" value={dashboard.expiry_pipeline['30_days']} color="text-orange-400" icon={<AlertTriangle className="w-4 h-4 text-orange-400" />} testId="expiry-30" />
                  <StatCard label="EXPIRED" value={dashboard.expiry_pipeline.expired} color="text-red-400" icon={<XCircle className="w-4 h-4 text-red-400" />} testId="expiry-expired" />
                </div>
              </div>

              {/* Deployment Risk */}
              <div>
                <h2 className="text-sm font-medium text-foreground mb-3">Deployment Risk</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard label="Authorised" value={dashboard.deployment_risk.authorised} color="text-emerald-400" icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} testId="dep-authorised" />
                  <StatCard label="Review" value={dashboard.deployment_risk.review} color="text-amber-400" icon={<Clock className="w-4 h-4 text-amber-400" />} testId="dep-review" />
                  <StatCard label="Blocked" value={dashboard.deployment_risk.blocked} color="text-red-400" icon={<XCircle className="w-4 h-4 text-red-400" />} testId="dep-blocked" />
                  <StatCard label="Exception Approved" value={dashboard.deployment_risk.exception_approved} color="text-sky-400" icon={<ShieldAlert className="w-4 h-4 text-sky-400" />} testId="dep-exception" />
                </div>
              </div>

              {/* Critical Controls */}
              {dashboard.critical_controls?.length > 0 && (
                <div>
                  <h2 className="text-sm font-medium text-foreground mb-3">Critical Controls</h2>
                  <div className="space-y-2">
                    {dashboard.critical_controls.map((cc, i) => (
                      <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg p-3" data-testid={`critical-${i}`}>
                        <span className="text-sm text-foreground font-medium">{cc.name}</span>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-emerald-400">{cc.green} GREEN</span>
                          <span className="text-amber-400">{cc.amber} AMBER</span>
                          <span className="text-red-400">{cc.red} RED</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Auto-Trigger Event Log */}
              {triggerLog.length > 0 && (
                <div>
                  <h2 className="text-sm font-medium text-foreground mb-3">Auto-Trigger Event Log</h2>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="auto-trigger-log">
                    {triggerLog.map((evt, i) => (
                      <div key={evt.id || i} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${evt.to_status === 'RED' ? 'bg-red-500/5 border border-red-500/20' : evt.to_status === 'AMBER' ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-muted/30'}`}
                        data-testid={`trigger-${i}`}>
                        <span className="text-[10px] text-muted-foreground w-32 flex-shrink-0">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : '-'}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${evt.to_status === 'RED' ? 'bg-red-500/10 text-red-400' : evt.to_status === 'AMBER' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>{evt.from_status} → {evt.to_status}</span>
                        <span className="text-foreground font-medium">{evt.staff_name}</span>
                        <span className="text-muted-foreground">{evt.requirement_name || evt.requirement_id}</span>
                        <span className="text-[10px] text-muted-foreground ml-auto">{evt.trigger_source?.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        )}

        {/* ====== REQUIREMENTS TAB ====== */}
        {tab === 'requirements' && (
          <div className="space-y-4" data-testid="requirements-section">
            <p className="text-sm text-muted-foreground">{requirements.length} compliance requirements configured</p>
            {requirements.length === 0 ? (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
                <p className="text-muted-foreground">No requirements configured</p>
                <p className="text-xs text-muted-foreground mt-1">Click "Seed Defaults" to populate standard UK care requirements</p>
              </div>
            ) : (
              <div className="space-y-2">
                {requirements.map(req => (
                  <Card key={req.id} className="glass-card border-border" data-testid={`req-${req.code}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-foreground">{req.name}</span>
                            <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 bg-muted rounded">{req.code}</span>
                            <span className="text-[10px] text-muted-foreground">{req.category}</span>
                          </div>
                          {req.description && <p className="text-[10px] text-muted-foreground mt-0.5">{req.description}</p>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {req.deployment_critical && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 font-semibold">DEPLOYMENT CRITICAL</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${req.criticality === 'CRITICAL' ? 'bg-red-500/10 text-red-400' : 'bg-muted text-muted-foreground'}`}>{req.criticality}</span>
                          {req.default_expiry_days && <span className="text-[10px] text-muted-foreground">{req.default_expiry_days}d expiry</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== ROLES TAB ====== */}
        {tab === 'roles' && (
          <div className="space-y-4" data-testid="roles-section">
            <p className="text-sm text-muted-foreground">{roles.length} roles configured</p>
            {roles.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
                <p className="text-muted-foreground">No roles configured</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {roles.map(role => (
                  <Card key={role.id} className="glass-card border-border" data-testid={`role-${role.code}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground">{role.name}</p>
                          <p className="text-[10px] text-muted-foreground">{role.code}</p>
                        </div>
                        {role.professional_registration_required && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0] font-semibold">REG REQUIRED</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== TRAINING TAB ====== */}
        {tab === 'training' && (
          <div className="space-y-4" data-testid="training-section">
            <p className="text-sm text-muted-foreground">{trainingReqs.length} training requirements</p>
            {trainingReqs.length === 0 ? (
              <div className="text-center py-8"><FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No training requirements</p></div>
            ) : (
              <div className="space-y-2">
                {trainingReqs.map(t => (
                  <Card key={t.id} className="glass-card border-border" data-testid={`training-${t.code}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <span className="text-sm font-medium text-foreground">{t.name}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">{t.category}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {t.mandatory && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">MANDATORY</span>}
                          {t.deployment_critical && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">DEPLOY CRITICAL</span>}
                          {t.default_frequency_days && <span className="text-[10px] text-muted-foreground">Every {t.default_frequency_days}d</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== WORKFORCE TAB ====== */}
        {tab === 'workforce' && (
          <div className="space-y-4" data-testid="workforce-section">
            <p className="text-sm text-muted-foreground">Select a staff member to view their compliance profile</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {staffList.map(s => (
                <Card key={s.id || s._id} className={`glass-card border-border cursor-pointer hover:border-[#00A6A0]/40 transition-colors ${selectedStaff === (s.id || s._id) ? 'border-[#00A6A0]/60 ring-1 ring-[#00A6A0]/30' : ''}`}
                  onClick={() => fetchStaffProfile(s.id || s._id)} data-testid={`staff-${s.id || s._id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#00A6A0]/20 text-[#00A6A0] flex items-center justify-center text-xs font-bold">
                        {(s.name || '?')[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{s.name}</p>
                        <p className="text-[10px] text-muted-foreground">{s.email}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {staffList.length === 0 && <p className="text-sm text-muted-foreground col-span-2 text-center py-8">No caregivers found in this organisation</p>}
            </div>

            {staffProfile && (
              <div className="space-y-4 mt-4" data-testid="staff-profile">
                <h2 className="text-sm font-medium text-foreground">Compliance Profile: {staffProfile.staff_name}</h2>
                <div className="grid grid-cols-4 gap-3">
                  <StatCard label="GREEN" value={staffProfile.summary.green} color="text-emerald-400" testId="profile-green" />
                  <StatCard label="AMBER" value={staffProfile.summary.amber} color="text-amber-400" testId="profile-amber" />
                  <StatCard label="RED" value={staffProfile.summary.red} color="text-red-400" testId="profile-red" />
                  <StatCard label="GREY" value={staffProfile.summary.grey} color="text-zinc-400" testId="profile-grey" />
                </div>
                <div className="space-y-2">
                  {staffProfile.compliance_records.map(r => (
                    <div key={r.id} className="flex items-center justify-between bg-muted/50 rounded-lg p-2" data-testid={`comp-${r.id}`}>
                      <div>
                        <span className="text-xs text-foreground">{r.requirement_id}</span>
                        <span className="text-[10px] text-muted-foreground ml-2">Evidence: {r.evidence_status}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.deployment_critical && <span className="text-[10px] text-red-400">CRITICAL</span>}
                        <StatusBadge status={r.status} />
                      </div>
                    </div>
                  ))}
                  {staffProfile.compliance_records.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No compliance records yet</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====== DEPLOYMENT GATE TAB ====== */}
        {tab === 'deployment' && (
          <div className="space-y-4" data-testid="deployment-section">
            <Card className="glass-card border-[#00A6A0]/30">
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-[#00A6A0] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">Server-Side Deployment Gate</p>
                    <p className="text-[10px] text-muted-foreground">Every shift assignment is checked against the worker's compliance profile. Critical RED controls = BLOCKED. No UI override possible. Every decision is immutably audited.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground">{deploymentChecks.length} recent deployment checks</p>
            {deploymentChecks.length === 0 ? (
              <div className="text-center py-8"><Lock className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No deployment checks yet</p></div>
            ) : (
              <div className="space-y-2">
                {deploymentChecks.map(dc => (
                  <Card key={dc.id} className="glass-card border-border" data-testid={`check-${dc.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-foreground font-medium">Staff: {dc.staff_id?.slice(-6)}</span>
                            <DecisionBadge decision={dc.decision} />
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(dc.checked_at).toLocaleString()}</p>
                          {dc.blocking_reasons?.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {dc.blocking_reasons.map((br, i) => (
                                <p key={i} className="text-[10px] text-red-400">BLOCK: {br.name || br.requirement} - {br.reason}</p>
                              ))}
                            </div>
                          )}
                          {dc.warnings?.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {dc.warnings.map((w, i) => (
                                <p key={i} className="text-[10px] text-amber-400">WARN: {w.name || w.requirement} - {w.status} {w.days_remaining != null ? `(${w.days_remaining}d)` : ''}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== EXCEPTIONS TAB ====== */}
        {tab === 'exceptions' && (
          <div className="space-y-4" data-testid="exceptions-section">
            <Card className="glass-card border-amber-500/30">
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">Compliance Exceptions</p>
                    <p className="text-[10px] text-muted-foreground">Exceptions are never hidden overrides. Each must have a named approver, reason, risk assessment, conditions, expiry, and full audit trail.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground">{exceptions.length} exceptions</p>
            {exceptions.length === 0 ? (
              <div className="text-center py-8"><ShieldAlert className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No exceptions requested</p></div>
            ) : (
              <div className="space-y-2">
                {exceptions.map(ex => (
                  <Card key={ex.id} className={`glass-card border-border ${ex.status === 'APPROVED' ? 'border-emerald-500/20' : ex.status === 'REJECTED' ? 'border-red-500/20' : 'border-amber-500/20'}`} data-testid={`exception-${ex.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground">Staff: {ex.staff_id?.slice(-6)}</span>
                            <span className="text-[10px] text-muted-foreground">Req: {ex.requirement_id}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{ex.reason}</p>
                          {ex.conditions && <p className="text-[10px] text-amber-400 mt-0.5">Conditions: {ex.conditions}</p>}
                          <p className="text-[10px] text-muted-foreground">Expires: {ex.expires_at ? new Date(ex.expires_at).toLocaleDateString() : 'N/A'}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          ex.status === 'APPROVED' ? 'bg-emerald-500/10 text-emerald-400' :
                          ex.status === 'REJECTED' ? 'bg-red-500/10 text-red-400' :
                          'bg-amber-500/10 text-amber-400'
                        }`}>{ex.status}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== ROLE-REQUIREMENT MATRIX TAB ====== */}
        {tab === 'matrix' && (
          <div className="space-y-4" data-testid="matrix-section">
            <p className="text-sm text-muted-foreground">Select a role to configure which compliance requirements apply</p>
            <div className="flex gap-2 flex-wrap">
              {roles.map(role => (
                <Button key={role.id} size="sm" variant={matrixRole === role.id ? 'default' : 'outline'}
                  className={matrixRole === role.id ? 'bg-[#00A6A0] text-white' : 'border-border'}
                  onClick={() => { setMatrixRole(role.id); fetchRoleReqs(role.id); }}
                  data-testid={`matrix-role-${role.code}`}>
                  {role.name}
                </Button>
              ))}
            </div>

            {matrixRole && (
              <Card className="glass-card border-border">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-foreground mb-3">
                    Requirements for: {roles.find(r => r.id === matrixRole)?.name}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {requirements.map(req => {
                      const isActive = roleReqs.some(rr => rr.requirement_id === req.id || rr.requirement_id === req.code);
                      return (
                        <div key={req.id} className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${isActive ? 'bg-[#00A6A0]/10 border border-[#00A6A0]/30' : 'bg-muted/30 border border-transparent hover:border-border'}`}
                          onClick={() => toggleRoleReq(matrixRole, req.id, isActive)}
                          data-testid={`matrix-${req.code}`}>
                          <div className="flex items-center gap-2">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isActive ? 'bg-[#00A6A0] border-[#00A6A0]' : 'border-muted-foreground'}`}>
                              {isActive && <CheckCircle2 className="w-3 h-3 text-white" />}
                            </div>
                            <div>
                              <span className="text-xs text-foreground">{req.name}</span>
                              <span className="text-[10px] text-muted-foreground ml-1">({req.code})</span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {req.deployment_critical && <span className="text-[10px] px-1 py-0.5 rounded bg-red-500/10 text-red-400">CRITICAL</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ====== BULK IMPORT TAB ====== */}
        {tab === 'import' && (
          <div className="space-y-4" data-testid="import-section">
            <Card className="glass-card border-[#00A6A0]/30">
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <Upload className="w-4 h-4 text-[#00A6A0] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">Bulk Staff Import</p>
                    <p className="text-[10px] text-muted-foreground">
                      Paste CSV data to import staff members with compliance records. Required columns: <code className="text-[#00A6A0]">first_name, last_name, email</code>.
                      Optional: <code className="text-[#00A6A0]">role_code, employment_type, start_date, dbs_status, dbs_expiry, rtw_status, training_status, training_expiry</code>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardContent className="p-4 space-y-3">
                <p className="text-xs text-muted-foreground">Example CSV:</p>
                <pre className="text-[10px] bg-muted p-2 rounded-lg overflow-x-auto text-muted-foreground">
{`first_name,last_name,email,role_code,employment_type,dbs_status,dbs_expiry,rtw_status,training_status,training_expiry
Jane,Smith,jane@example.com,DCW,EMPLOYEE,GREEN,2027-06-01,GREEN,GREEN,2027-01-15
John,Doe,john@example.com,HCA,BANK,AMBER,2026-11-01,GREEN,AMBER,2026-10-01`}
                </pre>
                <textarea value={csvText} onChange={e => setCsvText(e.target.value)}
                  placeholder="Paste CSV data here..."
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[150px] font-mono"
                  data-testid="csv-input" />
                <Button className="btn-primary" onClick={runBulkImport} disabled={importing || !csvText.trim()} data-testid="run-import-btn">
                  <Upload className="w-4 h-4 mr-1" />{importing ? 'Importing...' : 'Import Staff'}
                </Button>
              </CardContent>
            </Card>

            {importResult && (
              <Card className="glass-card border-emerald-500/30" data-testid="import-result">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-foreground mb-2">Import Results</p>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center"><p className="text-lg font-bold text-emerald-400">{importResult.imported}</p><p className="text-[10px] text-muted-foreground">Imported</p></div>
                    <div className="text-center"><p className="text-lg font-bold text-[#00A6A0]">{importResult.compliance_created}</p><p className="text-[10px] text-muted-foreground">Compliance Records</p></div>
                    <div className="text-center"><p className="text-lg font-bold text-amber-400">{importResult.skipped}</p><p className="text-[10px] text-muted-foreground">Skipped</p></div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Notes:</p>
                      {importResult.errors.slice(0, 10).map((e, i) => <p key={i} className="text-[10px] text-amber-400">{e}</p>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ====== DOCUMENTS TAB ====== */}
        {tab === 'documents' && (
          <div className="space-y-4" data-testid="documents-section">
            <Card className="glass-card border-[#00A6A0]/30">
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-[#00A6A0] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">
                      {docList?.region_label ? `${docList.region_label} HR Compliance Document Pack` : 'HR Compliance Document Pack v2.0'}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {docList?.total_documents ? `${docList.total_documents} documents` : 'Policy documents, forms, templates, and workflow specifications'} tailored to your region's regulatory framework. All documents are editable Word format branded with your organisation's details.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {!docList ? (
              <div className="text-center py-8"><FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">Loading documents...</p></div>
            ) : docList.paywall ? (
              <Card className="border-amber-500/30 bg-amber-500/5">
                <CardContent className="p-6 text-center space-y-4">
                  <Lock className="w-10 h-10 text-amber-500 mx-auto" />
                  <h3 className="text-lg font-medium text-foreground">HR Compliance Subscription Required</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Access to HR compliance documents, deployment gate, and compliance tools requires an active subscription at <strong>£5/user/month</strong>.
                  </p>
                  <Button className="btn-primary" onClick={() => window.location.href = '/subscription'} data-testid="subscribe-hr-btn">
                    Subscribe Now
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  {docList.total_documents} documents available
                  {docList.region_label && <span className="ml-1 text-[#00A6A0]">({docList.region_label})</span>}
                </p>
                {Object.entries(docList.categories).map(([key, cat]) => (
                  <div key={key}>
                    <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#00A6A0]" />{cat.label}
                      <span className="text-[10px] text-muted-foreground">({cat.count})</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cat.files.map(f => (
                        <a key={f.name} href={`${API}${f.download_url}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg hover:bg-muted/60 transition-colors group" data-testid={`doc-${f.name.replace(/\s/g, '-').slice(0, 30)}`}>
                          <Download className="w-3.5 h-3.5 text-muted-foreground group-hover:text-[#00A6A0] flex-shrink-0" />
                          <span className="text-xs text-foreground group-hover:text-[#00A6A0] truncate">{f.name.replace('.docx', '').replace(/_/g, ' ')}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
